import { UUID } from 'node:crypto';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { IIncidentCode, IIncidentCodesByGroup } from '@shared/services/incident-code-service';
import { IMesUser } from '@shared/services/mesuser.service';
import { IItemOperation } from '@shared/services/production-data.service';
import { IProductionNotePostRequest } from '@shared/services/production-note.service';
import { IPostStockReorderRequest, IStockReorderWithNotePostRequest, StockReorderService } from '@shared/services/stock-reorder.service';

export interface IStockReorderView {
	id: UUID;
	partNumber: string;
	title: string;
	handled: boolean;
	incidentCode: IIncidentCode;
	note?: string;
	employee?: string;
	sequenceNumber: number;
	itemAlias: string;
	batchId: UUID;
}

@Component({
	selector: 'app-terminal-create-stock-reorder-dialog',
	templateUrl: './terminal-create-stock-reorder-dialog.component.html',
	styleUrls: ['./terminal-create-stock-reorder-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TerminalCreateStockReorderDialogComponent implements OnInit {
	@Input() itemOperation: IItemOperation;
	@Input() employees: IMesUser[];
	@Input() incidentCodesByGroup: IIncidentCodesByGroup[];
	@Output() reordered: EventEmitter<any> = new EventEmitter();
	form: FormGroup;
	httpError: string = '';
	reordableSequenceNumbers: number[];
	loading: boolean = false;
	reordertypes = ['Glas', 'Alu profil', 'Andet'];
	stockReorderView: IStockReorderView;

	constructor(
		private dialogRef: MatDialogRef<TerminalCreateStockReorderDialogComponent>,
		private fb: FormBuilder,
		private stockReorderService: StockReorderService,
	) {}

	ngOnInit(): void {
		this.reordableSequenceNumbers = this.itemOperation?.items?.filter((item) => !item.reordered && !item.completed && item.ready).map((item) => item.sequenceNumber);

		this.stockReorderView = {
			id: null,
			partNumber: '',
			title: '',
			handled: false,
			incidentCode: null,
			note: '',
			employee: null,
			itemAlias: this.itemOperation.alias,
			sequenceNumber: this.reordableSequenceNumbers[0],
			batchId: this.itemOperation.batchId,
		};

		this.initializeForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			partNumber: [this.stockReorderView.partNumber, [Validators.required]],
			title: [this.stockReorderView.title, [Validators.required]],
			handled: [this.stockReorderView.handled],
			incidentCode: [this.stockReorderView.incidentCode, [Validators.required]],
			note: [this.stockReorderView.note],
			employee: [this.stockReorderView.employee, [Validators.required, (control: { value: string }) => (!control.value || !this.employees || this.employees.some((employee) => employee.alias === control.value) ? null : { employeeNotFound: true })]],
			sequenceNumber: [this.stockReorderView.sequenceNumber, [Validators.required]],
		});

		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.stockReorderView) {
					this.stockReorderView[key] = value[key];
				}
			});
		});

		if (!this.reordableSequenceNumbers?.length) {
			this.form.disable();
		}
	}

	lastSelectedValue: string = '';
	onSelectionChange() {
		const noteControl = this.form.controls['note'];
		const incidentControl = this.form.controls['incidentCode'];
		const noteControlValue: string = noteControl.value;

		if (noteControlValue === '' || noteControlValue.startsWith(this.lastSelectedValue)) {
			const inci: IIncidentCode = incidentControl.value;
			const newValue = `(${inci.alias}) ${inci.name}\n`;
			noteControl.setValue(newValue + noteControlValue.substring(this.lastSelectedValue.length));
			this.lastSelectedValue = newValue;
		}
	}

	onSave(): void {
		if (!this.form.valid) return;

		if (this.loading) return;

		this.loading = true;

		const incidentCode = this.stockReorderView.incidentCode;

		const stockReorderPost: IPostStockReorderRequest = {
			batchId: this.itemOperation.batchId,
			itemAlias: this.itemOperation.alias,
			terminalId: this.itemOperation.terminalId,
			incidentCodeId: incidentCode.id,
			reorderPartNumber: this.stockReorderView.partNumber,
			sequenceNumber: this.stockReorderView.sequenceNumber,
			title: this.stockReorderView.title,
		};

		const productionNotePost: IProductionNotePostRequest = {
			createdBy: this.stockReorderView.employee,
			itemAlias: this.stockReorderView.itemAlias,
			note: this.stockReorderView.note === '' ? `(${incidentCode.alias}) ${incidentCode.name}` : this.stockReorderView.note.replace(/[\r\n]+$/, ''),
			sequenceNumber: this.stockReorderView.sequenceNumber,
			batchId: this.stockReorderView.batchId,
		};

		const stockReorderWithNote: IStockReorderWithNotePostRequest = {
			externalReorder: stockReorderPost,
			productionNote: productionNotePost,
		};

		this.stockReorderService.createStockReorder(stockReorderWithNote).subscribe({
			next: () => {
				this.reordered.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
				this.loading = false;
			},
		});
	}

	onCancel = () => this.dialogRef.close();

	get controls() {
		return this.form.controls;
	}

	get incidentCodeControl(): FormControl<IIncidentCode | null> {
		return this.form.controls['incidentCode'] as FormControl<IIncidentCode | null>;
	}
}
